import {
  Box,
  Button,
  Checkbox,
  Flex,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { flexRender, getCoreRowModel, getSortedRowModel, useReactTable, createColumnHelper } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { DeleteIcon, EditIcon } from '@chakra-ui/icons';
import isEmpty from 'lodash/isEmpty';
import { FORMAT_DATE, ModalType } from 'constants/common';
import { toast } from 'components/Toast';
import { useDeleteScriptMutation, useDeleteScriptMutilMutation } from 'services/category';
import { formatDate } from 'utils/helpers';
import AuthorizationComponentWrapper from 'components/AuthorizationComponentWrapper/AuthorizationComponentWrapper';
import { useUserState } from 'context/UserContext';
import { FaCopy } from 'react-icons/fa';

const SizeTable = ({ categoryData, handleUpdateCategory, refetch }) => {
  const [sorting, setSorting] = useState([]);
  const columnHelper = createColumnHelper();
  const deleteCategoryMutation = useDeleteScriptMutation();
  const deleteMutilScriptMutation = useDeleteScriptMutilMutation();
  const { userInfo } = useUserState();
  const handleRowClick = (shortlink, type) => {
    handleUpdateCategory(shortlink, type);
  };
  const handleDeleteSize = async category => {
    const confirmDelete = window.confirm('Bạn có chắc chắn muốn xóa script này không?');
    if (!confirmDelete) {
      return;
    }
    deleteCategoryMutation.mutate(
      { urlId: category?._id },
      {
        onSuccess: () => {
          toast.showMessageSuccess('Xóa script thành công');
          refetch?.();
        },
        onError: () => {
          toast.showMessageError('Xóa script không thành công');
          refetch?.();
        },
      }
    );
  };

  const [rowSelection, setRowSelection] = useState([]);
  const onRowSelectionChange = newSelection => setRowSelection(newSelection);
  const columns = useMemo(
    () => [
      columnHelper.accessor('_id', {
        header: (
          <input
            type="checkbox"
            onChange={e => {
              e.stopPropagation();
              table.toggleAllRowsSelected(e.target.checked); //toggleAllPageRowsSelected xóa 1 page
            }}
          />
        ),
        cell: info => (
          <Box w={'fit-content'}>
            <input type="checkbox" checked={info.row.getIsSelected()} onChange={() => info.row.toggleSelected()} />
          </Box>
        ),
      }),
      // columnHelper.accessor('name', {
      //   header: 'Tên chiến dịch',
      //   cell: info => info.getValue(),
      // }),
      columnHelper.accessor('url', {
        header: 'Đường dẫn',
        cell: info => (
          <Flex alignItems={'center'} gap={3}>
            <Popover trigger="hover">
              <PopoverTrigger>
                <a href={info.getValue()} target="_blank">
                  <Text maxW={'300px'} noOfLines={2}>
                    {info.getValue()}
                  </Text>
                </a>
              </PopoverTrigger>
              <Portal>
                <PopoverContent>
                  <PopoverArrow background={'#ccc'} />
                  <PopoverCloseButton />
                  <PopoverBody background={'#ccc'} color={'black'}>
                    {info.getValue()}
                  </PopoverBody>
                </PopoverContent>
              </Portal>
            </Popover>
            <FaCopy
              size={15}
              cursor={'pointer'}
              onClick={() => {
                navigator.clipboard.writeText(info.getValue());
              }}
            />
          </Flex>
        ),
      }),

      // columnHelper.accessor('clicked', {
      //   header: 'Số lượt click',
      //   cell: info => info.getValue(),
      // }),
      columnHelper.accessor('createdAt', {
        header: 'Ngày tạo',
        cell: info => <Text whiteSpace={'nowrap'}>{formatDate(info.row.original.createdAt, FORMAT_DATE)}</Text>,
      }),

      columnHelper.accessor('action', {
        header: 'Thao tác',
        cell: info => (
          <AuthorizationComponentWrapper groupPermission={userInfo?.permissions} groupName={'UPDATE_PRODUCT_CATEGORY'}>
            <Flex alignItems="center" gap={1}>
              <IconButton
                bg="transparent"
                onClick={() => {
                  handleRowClick(info?.row?.original, ModalType.Add);
                }}
              >
                <EditIcon cursor="pointer" boxSize={4} />
              </IconButton>
              {/* <IconButton
              bg="transparent"
              onClick={() => {
                history.push(`/admin/category/${info?.row?.original?._id}/size`);
                }}
                >
              <TbTextResize size={16} />
              </IconButton>
              <IconButton
              bg="transparent"
              onClick={() => {
                history.push(`/admin/category/${info?.row?.original?._id}/colors`);
                }}
                >
                <MdOutlineColorLens size={16} />
                </IconButton> */}
              <IconButton
                bg="transparent"
                onClick={() => {
                  handleDeleteSize(info?.row?.original);
                }}
              >
                <DeleteIcon color="red.400" boxSize={4} />
              </IconButton>
            </Flex>
          </AuthorizationComponentWrapper>
        ),
      }),
    ],
    [categoryData]
  );

  var table = useReactTable({
    data: categoryData || [],
    columns,
    state: {
      sorting,
      rowSelection,
    },
    onSortingChange: setSorting,
    onRowSelectionChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
    enableRowSelection: true,
    getRowId: row => {
      return row._id;
    },
  });

  const handleDeleteShortlink = () => {
    if (Object.keys(rowSelection)?.length === 0) {
      toast.showMessageError('Vui lòng chọn script để xóa');
      return;
    }
    const confirmDelete = window.confirm('Bạn có chắc chắn muốn xóa các script này không?');
    if (!confirmDelete) {
      return;
    }
    deleteMutilScriptMutation.mutate(
      { ids: Object.keys(rowSelection) },
      {
        onSuccess: () => {
          toast.showMessageSuccess('Xóa script thành công');
          refetch?.();
          setRowSelection([]);
        },
        onError: () => {
          toast.showMessageError('Xóa script không thành công');
          refetch?.();
        },
      }
    );
  };

  return (
    <>
      <Table>
        <Thead>
          {table.getHeaderGroups().map(headerGroup => (
            <Tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <Th key={header.id}>
                  {header.isPlaceholder ? null : (
                    <Box w={'fit-content'} cursor={header.column.getCanSort() ? 'pointer' : 'default'}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {/* {{
                        asc: ' 🔼',
                        desc: ' 🔽',
                      }[header.column.getIsSorted()] ?? null} */}
                    </Box>
                  )}
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody>
          {isEmpty(table.getRowModel().rows) ? (
            <Tr>
              <Td textAlign="center" colSpan={5}>
                Không có dữ liệu
              </Td>
            </Tr>
          ) : (
            table.getRowModel().rows.map(row => (
              <Tr key={row.id}>
                {row.getVisibleCells().map(cell => (
                  <Td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</Td>
                ))}
              </Tr>
            ))
          )}
        </Tbody>
      </Table>
      <Flex alignItems={'center'} gap={3}>
        <Button w={'fit-content'} onClick={handleDeleteShortlink} isDisabled={Object.keys(rowSelection)?.length === 0}>
          Xóa script đã chọn
        </Button>
      </Flex>
    </>
  );
};

export default SizeTable;
